import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { BehaviorSubject, EMPTY, Observable, combineLatest, throwError } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';

import { ImageResolution } from 'helpers';
import { ApiModel, ImageModel } from 'models';
import { ModalService, SegmentService, UserService, WindowService } from 'services';
import { DynamicFeatureFlagService } from 'shared/services';
import { DownloadInitiator } from 'services/tracking/download-initiator';
import { restEndpoint } from 'app/config/backend';
import { ITEMS_PER_PAGE_CONTENT } from 'app/content/retailer-platform/content/constants';
import { getVideoThumbnailUrl } from 'app/content/helpers/marketing-files';
import { BulkDownloadImagesInformation } from 'app/content/models/product.model';
import { ENABLE_UNIFIED_CSV_DOWNLOAD_FLAG } from 'app/content/constants/csv-download.constants';
import {
    PRODUCT_VIDEO_PLACEHOLDER_CAMERA_SRC,
    getProductVideoThumbnailUrl
} from 'app/content/shared/product-video-placeholder.util';
import { isDownloadListVideoRow } from 'app/content/shared/product-download-list.util';
import { BulkDownloadToastService } from './bulk-download-toast.service';

@Injectable()
export class DownloadsService {
    private productsDownloadList = new Map<string, DownloadListItem>();
    private marketingDownloadList = new Map<string, DownloadListItem>();

    private downloadListUpdated: BehaviorSubject<void>;
    private downloadListLimit = ITEMS_PER_PAGE_CONTENT;

    constructor(
        private imageModel: ImageModel,
        private windowService: WindowService,
        private segmentService: SegmentService,
        private modalService: ModalService,
        private apiModel: ApiModel,
        private userService: UserService,
        private dynamicFeatureFlagService: DynamicFeatureFlagService,
        private bulkDownloadToastService: BulkDownloadToastService
    ) {
        this.downloadListUpdated = new BehaviorSubject<void>(null);
    }

    restore() {
        this.reset();
    }

    getCount = () => {
        const products = Array.from(this.productsDownloadList.values());
        const count = _.uniqBy(products, '_id').length + this.marketingDownloadList.size;
        return count;
    };

    getBrandCount = () => {
        const products = Array.from(this.productsDownloadList.values());
        const marketingFiles = Array.from(this.marketingDownloadList.values());
        const productBrands = _.map(products, 'brand._id');
        const marketingBrands = _.map(marketingFiles, 'brand._id');
        const brands = _.uniq([...productBrands, ...marketingBrands]);
        return brands.length;
    };

    getProductsCountByBrandVariant = (brandId: string) => {
        const products = Array.from(this.productsDownloadList.values());
        const productsByBrand = _.filter(products, (item) => item.brand._id === brandId);
        return _.uniq(_.map(productsByBrand, 'styleColorGroupId')).length;
    };

    getMarketingFilesCountByBrandFolder = (brandId: string, category: string) => {
        const marketingFiles = Array.from(this.marketingDownloadList.values());
        const marketingFilesByBrands = _.filter(marketingFiles, (item) => item.brand._id === brandId);
        const marketingFilesByBrandsAndCategory = _.filter(
            marketingFilesByBrands,
            (item) => category === item.additionals.category
        );
        return marketingFilesByBrandsAndCategory.length;
    };

    clearProductsByBrand = (brandId: string) => this.clearByBrand(brandId, this.productsDownloadList);

    clearMarketingFilesByBrand = (brandId: string) => this.clearByBrand(brandId, this.marketingDownloadList);

    clearMarketingFilesByBrandCategory = (brandId: string, category: string) => {
        const marketingFiles = Array.from(this.marketingDownloadList.values());
        _.forEach(marketingFiles, (item) => {
            if (item.brand._id === brandId && item.additionals.category === category) {
                this.marketingDownloadList.delete(item.productId);
            }
        });
    };

    productsCartEmpty = () => _.isEmpty(this.productsDownloadList);

    toggleProduct = (item) => this.toggle(this.productsDownloadList)(item);

    toggleMarketingFile = (item) => this.toggle(this.marketingDownloadList)(item);

    containsProduct = (item: any) => this.contains(this.productsDownloadList)(item);

    containsMarketingFile = (item: any) => this.contains(this.marketingDownloadList)(item);

    getProducts = () => {
        const products = Array.from(this.productsDownloadList.values());
        return _.uniqBy(products, '_id');
    };

    getMarketingFiles = () => Array.from(this.marketingDownloadList.values());

    removeProduct = (productId: string) => {
        this.productsDownloadList.delete(productId);
    };

    removeMarketingImage = (productId: string) => {
        this.marketingDownloadList.delete(productId);
    };

    getBadgeCount = () => this.getCount();

    getDownloadListUpdated() {
        return this.downloadListUpdated.asObservable();
    }

    getIsDownloadLimitReached(): DownloadListFull {
        const { products, marketingImages } = this.getDownloadListTotalCountByVariant();

        const isProductsLimitReached = products >= this.downloadListLimit;
        const isMarketingImagesLimitReached = marketingImages >= this.downloadListLimit;

        return {
            products: isProductsLimitReached,
            marketingImages: isMarketingImagesLimitReached
        };
    }

    handleFileResponse = ({ body: blob, headers }, fileNameFallback: string) => {
        const filename = headers.get('Content-Disposition').split('=')[1].replace(/\"/g, '');
        const downloadElement = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        downloadElement.href = objectUrl;
        downloadElement.download = Boolean(filename) ? filename : fileNameFallback;
        downloadElement.click();
        URL.revokeObjectURL(objectUrl);
    };

    getDownloadListTotalCountByVariant = (): SelectAllCount => {
        const products = Array.from(this.productsDownloadList.values());
        const styleColorGroupIds = _.map(products, 'styleColorGroupId');
        return {
            products: _.uniq(styleColorGroupIds).length,
            marketingImages: this.marketingDownloadList.size
        };
    };

    getDownloadListMediaCounts = (): { imageCount: number; videoCount: number } => {
        const products = Array.from(this.productsDownloadList.values());
        const marketingFiles = Array.from(this.marketingDownloadList.values());

        const productVideoCount = products.filter((item) => isDownloadListVideoRow(item)).length;
        const productImageCount = products.filter((item) => !isDownloadListVideoRow(item)).length;
        const marketingVideoCount = marketingFiles.filter((item) => item.type === 'video').length;
        const marketingImageCount = marketingFiles.filter((item) => item.type !== 'video').length;

        return {
            imageCount: productImageCount + marketingImageCount,
            videoCount: productVideoCount + marketingVideoCount
        };
    };

    notifyDownloadListChanged() {
        this.downloadListUpdated.next();
    }

    downloadImages(images = true, video = false, size: ImageResolution = ImageResolution.Original) {
        return this.downloadObservable({ images, video, size })
            .pipe(
                tap((response) => this.handleDownloadResponse(response)),
                catchError((error) => this.handleDownloadError(error))
            )
            .toPromise();
    }

    downloadProductData(csvLanguage?: string) {
        return this.isUnifiedCsvDownloadEnabled$()
            .pipe(
                switchMap((enabled) =>
                    enabled
                        ? this.downloadObservable({ includeMedia: false, includeCsv: true, csvLanguage }).pipe(
                              tap((response) => this.handleDownloadResponse(response, csvLanguage))
                          )
                        : this.downloadProductDataObservable().pipe(
                              tap(({ downloadId }) => this.navigateToSeparateCsvDownload(downloadId))
                          )
                ),
                catchError((error) => this.handleDownloadError(error))
            )
            .toPromise();
    }

    downloadImagesAndProductData(
        images = true,
        video = false,
        size: ImageResolution = ImageResolution.Original,
        csvLanguage?: string
    ) {
        return this.isUnifiedCsvDownloadEnabled$()
            .pipe(
                switchMap((enabled) =>
                    enabled
                        ? this.downloadObservable({ images, video, size, includeCsv: true, csvLanguage }).pipe(
                              tap((response) => this.handleDownloadResponse(response, csvLanguage))
                          )
                        : combineLatest([
                              this.downloadObservable({ images, video, size }),
                              this.downloadProductDataObservable(size)
                          ]).pipe(
                              tap(([{ downloadId }, { downloadId: csvDownloadId }]) =>
                                  this.navigateToDownloadWithSeparateCsv(downloadId, csvDownloadId)
                              )
                          )
                ),
                catchError((error) => this.handleDownloadError(error))
            )
            .toPromise();
    }

    private isUnifiedCsvDownloadEnabled$(): Observable<boolean> {
        return this.dynamicFeatureFlagService.isFeatureEnabled$(ENABLE_UNIFIED_CSV_DOWNLOAD_FLAG).pipe(take(1));
    }

    private navigateToSeparateCsvDownload(downloadId: string) {
        this.windowService.location = `${restEndpoint}/internal${this.apiModel.getProductsEndpoint()}/data/${downloadId}`;
    }

    private navigateToDownloadWithSeparateCsv(downloadId: string, csvDownloadId: string) {
        this.windowService.location = `${restEndpoint}/internal${this.apiModel.getDownloadsEndpoint()}/${downloadId}?csvToken=${csvDownloadId}`;
    }

    private handleDownloadError(error: { data?: { type?: string } }): Observable<never> {
        if (error?.data?.type === 'AccountLimitReachedError') {
            this.modalService.open('upgrade-plan');
        } else {
            void this.bulkDownloadToastService.showInternalError();
        }

        return EMPTY;
    }

    private handleDownloadResponse(
        {
            downloadId,
            downloadInformation
        }: {
            downloadId?: string;
            downloadInformation?: Pick<BulkDownloadImagesInformation, 'csvOmitted' | 'csvOmissionReasons'>;
        },
        csvLanguage?: string
    ) {
        if (!downloadId) {
            // The backend created no download, for example because PCS could not generate the only requested CSV.
            void this.bulkDownloadToastService.showInternalError();
            return;
        }

        if (downloadInformation?.csvOmitted) {
            void this.bulkDownloadToastService.showCsvOmitted();
        }

        if (downloadInformation?.csvOmissionReasons?.length) {
            void this.bulkDownloadToastService.showCsvSkipped(downloadInformation.csvOmissionReasons);
        }

        const languageQuery = csvLanguage === 'all' ? '?lang=all' : '';
        this.windowService.location = `${restEndpoint}/internal${this.apiModel.getDownloadsEndpoint()}/${downloadId}${languageQuery}`;
    }

    private downloadObservable({
        images = true,
        video = false,
        size = ImageResolution.Original,
        includeMedia = true,
        includeCsv = false,
        csvLanguage
    }: {
        images?: boolean;
        video?: boolean;
        size?: ImageResolution;
        includeMedia?: boolean;
        includeCsv?: boolean;
        csvLanguage?: string;
    }) {
        const cartRows = Array.from(this.productsDownloadList.values());
        const videoRows = cartRows.filter(isDownloadListVideoRow);
        const imageRows = cartRows.filter((item) => !isDownloadListVideoRow(item));

        const productImages =
            includeMedia && images
                ? _.map(imageRows, ({ fileId, groupedProducts }) => ({
                      fileId,
                      gtin: groupedProducts[0].gtin
                  }))
                : [];

        const videoGtins = includeMedia && video ? _.uniq(_.compact(_.map(videoRows, 'groupedProducts[0].gtin'))) : [];

        const marketingFiles = includeMedia
            ? _.map(
                  _.filter(Array.from(this.marketingDownloadList.values()), ({ _id }) => _id),
                  '_id'
              )
            : [];

        return this.apiModel
            .makeHttpObservableRequest({
                method: 'POST',
                url: `${this.apiModel.getDownloadsEndpoint()}/request`,
                body: {
                    productImages,
                    marketingFiles,
                    logos: [],
                    initiator: DownloadInitiator.SummaryBar,
                    size,
                    ...(includeMedia ? { images, video } : {}),
                    ...(includeCsv
                        ? { gtins: this.getSelectedGtins(), csv: this.getCsvOption(csvLanguage) }
                        : !_.isEmpty(videoGtins)
                          ? { gtins: videoGtins }
                          : {})
                }
            })
            .pipe(
                catchError((error) => {
                    if (!includeMedia) {
                        console.error('Could not download CSV file', error);
                        return throwError(error);
                    }

                    this.segmentService.track('Summary bar: download images failed', {
                        amount: productImages.length + marketingFiles.length,
                        error: error.data?.type,
                        size
                    });
                    return throwError(error);
                })
            );
    }

    private downloadProductDataObservable(imageSize: ImageResolution = ImageResolution.Original) {
        return this.apiModel
            .makeHttpObservableRequest({
                method: 'POST',
                url: `${this.apiModel.getProductsEndpoint()}/data/request`,
                body: {
                    gtins: this.getSelectedGtins(),
                    language: this.userService.getPreferredLanguage(),
                    imageSize,
                    initiator: DownloadInitiator.SummaryBar
                }
            })
            .pipe(
                catchError((error) => {
                    console.error('Could not download CSV file', error);
                    return throwError(error);
                })
            );
    }

    private getSelectedGtins(): string[] {
        return gtinsOfDownloadListItems(Array.from(this.productsDownloadList.values()));
    }

    private getCsvOption(csvLanguage?: string) {
        return { include: true, language: csvLanguage === 'all' ? 'all' : this.userService.getPreferredLanguage() };
    }

    private contains(downloadList: Map<string, DownloadListItem>) {
        return (item: any) => downloadList.has(item.productId);
    }

    private toggle(downloadList: Map<string, DownloadListItem>) {
        return (item: any) => {
            if (!downloadList.has(item.productId)) {
                const signedThumb = getProductVideoThumbnailUrl(item);
                const isVideoThumbnail = item.type === 'video' && item.hasPreview && item.thumbnailFilename;
                item.isVideoThumbnail = isVideoThumbnail;
                if (item.type === 'video') {
                    if (signedThumb) {
                        item.thumb = signedThumb;
                        item.useVideoPlaceholder = false;
                    } else if (isVideoThumbnail) {
                        item.thumb = getVideoThumbnailUrl(item.thumbnailFilename);
                        item.useVideoPlaceholder = false;
                    } else {
                        item.useVideoPlaceholder = true;
                        item.thumb = PRODUCT_VIDEO_PLACEHOLDER_CAMERA_SRC;
                    }
                } else {
                    item.useVideoPlaceholder = false;
                    item.thumb = this.imageModel.buildImageUrl(item.fileId, 'thumb');
                }

                downloadList.set(item.productId, item);
            } else {
                downloadList.delete(item.productId);
            }
        };
    }

    private clearByBrand = (brandId: string, downloadList: Map<string, DownloadListItem>) => {
        const downloadListItems = Array.from(downloadList.values());
        _.forEach(downloadListItems, (item) => {
            if (item.brand._id === brandId) {
                downloadList.delete(item.productId);
            }
        });
    };

    private reset() {
        this.productsDownloadList = new Map<string, DownloadListItem>();
        this.marketingDownloadList = new Map<string, DownloadListItem>();
    }
}

interface SelectAllCount {
    products: number;
    marketingImages: number;
}

interface DownloadListFull {
    products: boolean;
    marketingImages: boolean;
}

function gtinsOfDownloadListItems(items: DownloadListItem[]): string[] {
    return _.reduce(
        items,
        (gtinsAccumulator, product: any) => {
            const groupedGtins = _.map(product.groupedProducts, 'gtin');
            return _.concat(gtinsAccumulator, groupedGtins);
        },
        []
    );
}

type DownloadListItem = {
    productId: string;
    brand: {
        _id: string;
    };
    [key: string]: any;
};
